import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 disabled:hover:bg-primary disabled:hover:text-primary-foreground',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:hover:bg-destructive disabled:hover:text-destructive-foreground',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:hover:bg-background disabled:hover:text-current',
        secondary:
          'bg-primary/10 text-primary hover:bg-primary/20 disabled:hover:bg-primary/10 disabled:hover:text-primary',
        ghost:
          'hover:bg-accent hover:text-accent-foreground disabled:hover:bg-transparent disabled:hover:text-current',
        link: 'text-primary underline-offset-4 hover:underline disabled:hover:no-underline',
      },
      size: {
        // Text buttons — height scale: 24, 32, 36, 40, 48
        xs: 'h-6 px-2 text-xs [&_svg]:size-3',
        sm: 'h-8 px-3 text-xs [&_svg]:size-3.5',
        md: 'h-9 px-4 py-2 [&_svg]:size-4',
        lg: 'h-10 px-4 text-base [&_svg]:size-4',
        xl: 'h-12 px-8 text-base [&_svg]:size-5',
        // Icon-only buttons — square at each height tier
        'icon-xs': 'h-6 w-6 [&_svg]:size-3',
        'icon-sm': 'h-8 w-8 [&_svg]:size-4',
        'icon-md': 'h-9 w-9 [&_svg]:size-4',
        'icon-lg': 'h-10 w-10 [&_svg]:size-5',
        'icon-xl': 'h-12 w-12 [&_svg]:size-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Tooltip shown when the button is disabled. Explains why the action is unavailable. */
  disabledTooltip?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, disabledTooltip, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const button = (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={disabled && disabledTooltip ? undefined : ref}
        disabled={disabled}
        {...props}
      />
    );

    if (disabled && disabledTooltip) {
      return (
        <TooltipPrimitive.Provider delayDuration={300}>
          <TooltipPrimitive.Root>
            <TooltipPrimitive.Trigger asChild>
              <span ref={ref as React.Ref<HTMLSpanElement>} tabIndex={0} className="inline-flex">
                {button}
              </span>
            </TooltipPrimitive.Trigger>
            <TooltipPrimitive.Portal>
              <TooltipPrimitive.Content
                sideOffset={4}
                className="z-50 overflow-hidden rounded-md bg-tooltip px-3 py-1.5 text-xs text-tooltip-foreground animate-in fade-in-0 zoom-in-95"
              >
                {disabledTooltip}
              </TooltipPrimitive.Content>
            </TooltipPrimitive.Portal>
          </TooltipPrimitive.Root>
        </TooltipPrimitive.Provider>
      );
    }

    return button;
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
